import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { categories } from '@/mocks/comics';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileOpen(false);
  }, [location]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      window.REACT_APP_NAVIGATE?.(`/category/all?search=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery('');
      setShowSearch(false);
    }
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled || isMobileOpen
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
          : 'bg-transparent'
      }`}
    >
      <div className="px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2 shrink-0">
            <div className="w-9 h-9 flex items-center justify-center bg-accent-500 rounded-lg">
              <i className="ri-book-read-line text-lg text-background-50"></i>
            </div>
            <span className="text-lg font-bold font-heading text-foreground-950 whitespace-nowrap">
              韩国漫画大全
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-8">
            <Link
              to="/"
              className={`text-sm font-medium transition-colors whitespace-nowrap ${
                location.pathname === '/'
                  ? 'text-primary-500'
                  : 'text-foreground-700 hover:text-foreground-950'
              }`}
            >
              首页
            </Link>
            <Link
              to="/category/all"
              className={`text-sm font-medium transition-colors whitespace-nowrap ${
                location.pathname.startsWith('/category')
                  ? 'text-primary-500'
                  : 'text-foreground-700 hover:text-foreground-950'
              }`}
            >
              分类浏览
            </Link>
            <Link
              to="/category/all?status=completed"
              className={`text-sm font-medium transition-colors whitespace-nowrap ${
                location.pathname === '/category/all' && location.search.includes('completed')
                  ? 'text-primary-500'
                  : 'text-foreground-700 hover:text-foreground-950'
              }`}
            >
              完结大全
            </Link>
            <Link
              to="/category/all?sort=newest"
              className="text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors whitespace-nowrap"
            >
              每日更新
            </Link>
          </div>

          <div className="hidden md:flex items-center gap-3">
            {showSearch ? (
              <form onSubmit={handleSearch} className="flex items-center">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="搜索漫画..."
                  className="w-48 px-3 py-2 text-sm rounded-full border border-background-300 bg-background-100 text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-accent-400 transition-colors"
                  autoFocus
                />
                <button
                  type="button"
                  onClick={() => setShowSearch(false)}
                  className="ml-2 w-8 h-8 flex items-center justify-center rounded-full text-foreground-500 hover:text-foreground-700 transition-colors"
                >
                  <i className="ri-close-line"></i>
                </button>
              </form>
            ) : (
              <button
                onClick={() => setShowSearch(true)}
                className="w-9 h-9 flex items-center justify-center rounded-full text-foreground-600 hover:text-foreground-950 hover:bg-background-200 transition-colors"
                aria-label="搜索"
              >
                <i className="ri-search-line text-lg"></i>
              </button>
            )}
            <Link
              to="/about"
              className="text-sm font-medium text-foreground-600 hover:text-foreground-950 transition-colors whitespace-nowrap"
            >
              关于我们
            </Link>
          </div>

          <button
            className="md:hidden w-9 h-9 flex items-center justify-center rounded-lg text-foreground-700 hover:bg-background-200 transition-colors"
            onClick={() => setIsMobileOpen(!isMobileOpen)}
            aria-label="菜单"
          >
            <i className={`text-xl ${isMobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>

        {isMobileOpen && (
          <div className="md:hidden pb-4 border-t border-background-200/70 pt-3 space-y-2">
            <Link
              to="/"
              className="block px-3 py-2 rounded-lg text-sm font-medium text-foreground-700 hover:bg-background-200 transition-colors"
            >
              首页
            </Link>
            <Link
              to="/category/all"
              className="block px-3 py-2 rounded-lg text-sm font-medium text-foreground-700 hover:bg-background-200 transition-colors"
            >
              分类浏览
            </Link>
            <Link
              to="/category/all?status=completed"
              className="block px-3 py-2 rounded-lg text-sm font-medium text-foreground-700 hover:bg-background-200 transition-colors"
            >
              完结大全
            </Link>
            <Link
              to="/category/all?sort=newest"
              className="block px-3 py-2 rounded-lg text-sm font-medium text-foreground-700 hover:bg-background-200 transition-colors"
            >
              每日更新
            </Link>
            <Link
              to="/about"
              className="block px-3 py-2 rounded-lg text-sm font-medium text-foreground-700 hover:bg-background-200 transition-colors"
            >
              关于我们
            </Link>
            <form onSubmit={handleSearch} className="px-3 pt-2">
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="搜索漫画..."
                  className="flex-1 px-3 py-2 text-sm rounded-full border border-background-300 bg-background-100 text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-accent-400"
                />
                <button
                  type="submit"
                  className="w-9 h-9 flex items-center justify-center rounded-full bg-accent-500 text-background-50 hover:bg-accent-600 transition-colors"
                >
                  <i className="ri-search-line"></i>
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </nav>
  );
}